/**
 * DuressAlertBanner — surfaces unacknowledged duress-login events
 * to the real account holder on their NEXT primary login.
 *
 * Threat model: the coercer who just logged in with the duress
 * password saw only the decoy vault. When the real user later logs
 * in with their primary password, this banner tells them a duress
 * unlock happened (when / from where) so they can rotate their
 * primary password, wipe sessions, etc.
 *
 * Hides itself:
 *   - in duress mode (we must never call /auth/duress-alerts while
 *     the coercer is present — that endpoint's very existence in a
 *     network trace would reveal the decoy architecture)
 *   - when there are no unacknowledged alerts
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _fmt = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

export const DuressAlertBanner = () => {
  const { isDuressMode, user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (isDuressMode || !user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/duress-alerts`);
        if (!cancelled && Array.isArray(r.data)) setAlerts(r.data);
      } catch {
        /* No alerts, endpoint missing, or network hiccup — silent. */
      }
    })();
    return () => { cancelled = true; };
  }, [isDuressMode, user?.id]);

  if (isDuressMode || alerts.length === 0) return null;

  const top = alerts[0];

  const acknowledgeAll = async () => {
    if (dismissing) return;
    setDismissing(true);
    try {
      await Promise.all(
        alerts.map((a) =>
          axios.post(`${API}/auth/duress-alerts/${a.id}/acknowledge`).catch(() => {})
        )
      );
      setAlerts([]);
    } finally {
      setDismissing(false);
    }
  };

  return (
    <div
      className="mx-4 mt-3 mr-20 rounded-lg border border-amber-500/40 bg-amber-950/40 px-4 py-3 flex items-start gap-3"
      data-testid="duress-alert-banner"
      role="alert"
    >
      <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1 text-sm">
        <div className="font-semibold text-amber-200">
          Duress unlock detected
        </div>
        <div className="text-amber-100/80 mt-0.5">
          Your duress password was used to open a decoy session
          {top.triggered_at ? ` at ${_fmt(top.triggered_at)}` : ''}
          {top.ip_address ? ` from ${top.ip_address}` : ''}.
          {alerts.length > 1 ? ` (${alerts.length} events pending.)` : ''}
          {' '}Rotate your primary password and review active sessions if this wasn't you.
        </div>
      </div>
      <button
        onClick={acknowledgeAll}
        disabled={dismissing}
        className="text-amber-300/70 hover:text-amber-200 transition-colors p-1 disabled:opacity-50"
        data-testid="duress-alert-dismiss"
        aria-label="Dismiss duress alerts"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default DuressAlertBanner;
